import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ImageLightbox from './ImageLightbox';
import { useGuest } from '../contexts/GuestContext';
import { useEditMode } from '../contexts/EditModeContext';

function Gallery({ photos, allPhotos, albumId, onPhotoDeleted }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const { guestGroup } = useGuest();
  const { isEditMode } = useEditMode();
  const longPressTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const canDelete = isEditMode || guestGroup;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const photoParam = params.get('photo');
    
    if (photoParam && allPhotos && allPhotos.length > 0) {
      const index = allPhotos.findIndex(p => String(p.id) === String(photoParam));
      if (index !== -1) {
        setSelectedIndex(index);
      }
    } else if (!photoParam && selectedIndex !== null) {
      setSelectedIndex(null);
    }
  }, [location.search, allPhotos]);

  const handleIndexChange = (newIndex) => {
    setSelectedIndex(newIndex);
    const photoId = allPhotos[newIndex]?.id;
    if (photoId) {
      const params = new URLSearchParams(location.search);
      params.set('photo', photoId);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  };

  const openLightbox = (index) => {
    setSelectedIndex(index);
    const photoId = allPhotos[index]?.id;
    if (photoId) {
      const params = new URLSearchParams(location.search);
      params.set('photo', photoId);
      navigate(`${location.pathname}?${params.toString()}`, { replace: false });
    }
  };

  const closeLightbox = () => {
    setSelectedIndex(null);
    const params = new URLSearchParams(location.search);
    params.delete('photo');
    const newSearch = params.toString();
    navigate(`${location.pathname}${newSearch ? '?' + newSearch : ''}`, { replace: false });
  };

  const handlePhotoClick = (index, photoId, isMissing) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    
    if (selectMode) {
      togglePhotoSelection(photoId);
    } else if (isMissing) {
      setSelectMode(true);
      togglePhotoSelection(photoId);
    } else {
      openLightbox(index);
    }
  };

  const handleCheckboxClick = (photoId, e) => {
    e.stopPropagation();
    if (!selectMode) {
      setSelectMode(true);
    }
    togglePhotoSelection(photoId);
  };

  const handleTouchStart = (photoId, e) => {
    if (!selectMode && canDelete) {
      longPressTimer.current = setTimeout(() => {
        setSelectMode(true);
        setSelectedPhotos([photoId]);
        longPressTimer.current = null;
      }, 500);
    }
  };

  const handleTouchEnd = (e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => {
      const newSelection = prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId];
      
      if (newSelection.length === 0) {
        setSelectMode(false);
      }
      
      return newSelection;
    });
  };

  const handleDeleteClick = (photoId, e) => {
    e.stopPropagation();
    setDeleteConfirm([photoId]);
  };

  const handleDeleteSelected = () => {
    if (selectedPhotos.length > 0) {
      setDeleteConfirm(selectedPhotos);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      for (const photoId of deleteConfirm) {
        await fetch(`/api/photos/${photoId}`, {
          method: 'DELETE'
        });
      }

      if (onPhotoDeleted) {
        await onPhotoDeleted();
      }
      setSelectedPhotos([]);
      setSelectMode(false);
    } catch (error) {
      console.error('Failed to delete:', error);
    }
    setDeleteConfirm(null);
  };

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedPhotos([]);
  };

  return (
    <>
      {canDelete && selectMode && selectedPhotos.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button 
            className="btn" 
            onClick={handleDeleteSelected}
            style={{ 
              background: '#c77c7c', 
              border: '2px solid white',
              color: 'white',
              fontSize: '0.85rem', 
              padding: '0.4rem 0.8rem', 
              width: 'auto' 
            }}
          >
            Delete ({selectedPhotos.length})
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={toggleSelectMode}
            style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem', width: 'auto' }}
          >
            Cancel
          </button>
        </div>
      )}

      <div className="gallery-grid">
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            className={`photo-card ${selectedPhotos.includes(photo.id) ? 'photo-selected' : ''}`}
            onClick={() => handlePhotoClick(index, photo.id, photo.isMissing)}
            onTouchStart={(e) => handleTouchStart(photo.id, e)}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchEnd}
            style={{ cursor: 'pointer' }}
          >
            {photo.isMissing ? (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%', 
                color: '#999', 
                fontSize: '0.85rem', 
                textAlign: 'center', 
                padding: '1rem',
                flexDirection: 'column',
                gap: '0.5rem'
              }}>
                <div>⚠️</div>
                <div>Photo Missing</div>
                <div style={{ fontSize: '0.7rem' }}>ID: {photo.id}</div>
              </div>
            ) : photo.isVideo ? (
              <div style={{ position: 'relative' }}>
                <img 
                  src={photo.thumbnailUrl || photo.url} 
                  alt="" 
                  loading="lazy" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onContextMenu={(e) => e.preventDefault()}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999; font-size: 0.85rem; text-align: center; padding: 1rem;">Video not found</div>';
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '3rem',
                  color: 'white',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                  pointerEvents: 'none'
                }}>▶</div>
              </div>
            ) : (
              <img 
                src={photo.thumbnailUrl || photo.url} 
                alt="" 
                loading="lazy"
                onContextMenu={(e) => e.preventDefault()}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999; font-size: 0.85rem; text-align: center; padding: 1rem;">Image not found</div>';
                }}
              />
            )}
            {canDelete && (
              <div 
                className="photo-select-checkbox"
                onClick={(e) => handleCheckboxClick(photo.id, e)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.4)';
                  e.currentTarget.querySelector('.check-icon').style.color = 'rgba(255, 255, 255, 0.9)';
                }}
                onMouseLeave={(e) => {
                  if (!selectedPhotos.includes(photo.id)) {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.15)';
                  } else {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                  }
                  e.currentTarget.querySelector('.check-icon').style.color = 'white';
                }}
                style={{
                  position: 'absolute',
                  top: '0.5rem',
                  left: '0.5rem',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  border: '2px solid white',
                  background: selectedPhotos.includes(photo.id) ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  opacity: selectedPhotos.includes(photo.id) ? 1 : 0,
                  zIndex: 10
                }}
              >
                <span 
                  className="check-icon"
                  style={{ 
                    color: 'white', 
                    fontSize: '16px', 
                    fontWeight: 'bold',
                    transition: 'color 0.2s'
                  }}
                >
                  ✓
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {selectedIndex !== null && !selectMode && (
        <ImageLightbox
          photos={allPhotos}
          currentIndex={selectedIndex}
          onClose={closeLightbox}
          onIndexChange={handleIndexChange}
        />
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Delete {deleteConfirm.length > 1 ? `${deleteConfirm.length} Photos/Videos` : 'Photo/Video'}?</h2>
            <p>This action cannot be undone.</p>
            <div className="modal-buttons">
              <button className="btn" style={{ background: '#d32f2f' }} onClick={handleDeleteConfirm}>
                Delete
              </button>
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Gallery;
